import React, { useState, useEffect, useCallback } from "react";
import { MDBCard, MDBCardBody, MDBCardTitle, MDBCol, MDBIcon, MDBInput, MDBRow, MDBTable, MDBTableBody,
    MDBTableHead, MDBChip, MDBTextArea, MDBBtn, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody,
    MDBTabs,
    MDBTabsItem,
    MDBTabsLink,
    MDBTabsContent,
    MDBTabsPane,
    MDBSelect,
    MDBTypography,
    MDBTooltip} from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../utils/sentryHelpers';


const client = generateClient();
export default function EventGuests({company}) {
    const { t, i18n } = useTranslation();
    // Extract company ID from multiple possible sources with proper validation
    const companyid = (() => {
        // First, check if company prop is provided and has an id
        if (company && company.id) {
            return company.id;
        }

        // Otherwise, extract from URL pathname
        const pathname = window.location.pathname;

        // Handle /admin/company/:companyid pattern
        if (pathname.includes("/admin/company/")) {
            return pathname.split("/")[3];
        }

        // Handle /mycompany/:companyid pattern
        if (pathname.includes("/mycompany/")) {
            return pathname.split("/")[2];
        }

        // Default fallback (though this shouldn't happen)
        return null;
    })();

    const [isLoading, setIsLoading] = useState(false);
    const [codes, setCodes] = useState([]);
    const [usedCodes, setUsedCodes] = useState([]);
    const [events, setEvents] = useState([]);
    const [companyAttendingEventsIds, setCompanyAttendingEventsIds] = useState([]);
    const [companyBookedBooths, setCompanyBookedBooths] = useState([]);
    const [selectedEventForVisitorsId, setSelectedEventForVisitorsId] = useState("");
    const [allVisisions, setAllVisisions] = useState([]);
    const [guests, setGuests] = useState([]);

    const getCodes = async () => {
        // Validate companyid before making GraphQL query
        if (!companyid) {
            console.error("Cannot fetch codes: companyid is null or undefined");
            captureError(new Error("Missing companyid in getCodes"), {
                feature: 'event-management',
                operation: 'fetch-invitation-codes',
                context: { companyid, pathname: window.location.pathname }
            });
            return [];
        }

        try {
          const company = await client.graphql({
            variables: { id: companyid },
            query: /* GraphQL */ `
            query GetCompany($id: ID!) {
                getCompany(id: $id) {
                    id
                    invitationCodes {
                        items {
                            id
                            code
                            createdByAdmin
                            event {
                                id
                                name {
                                    text
                                    language
                                }
                                startDate
                                endDate

                            }
                            discountCode {
                                id
                                maxUsage
                                usageCount
                            }
                        }
                    }
                }
            }
            `
            });
            setCodes(company.data.getCompany.invitationCodes.items);
            // Check if code is used by searching for Tickets with the code, with limit 1.
            const usedCodesForCompany = [];
            try {
                for (const code of company.data.getCompany.invitationCodes.items) {
                    const tickets = await client.graphql({
                        variables: {
                            limit: 1,
                            filter: {
                                invitationCode: {
                                    eq: code.code
                                }
                            }
                        },
                        query: /* GraphQL */ `
                        query SearchTickets($filter: SearchableTicketFilterInput, $limit: Int) {
                            searchTickets(filter: $filter, limit: $limit) {
                                items {
                                    id
                                }
                            }
                        }
                        `
                    });
                    if (tickets.data.searchTickets.items.length > 0) {
                        usedCodesForCompany.push(code.id);
                    }
                    
                }
                setUsedCodes(usedCodesForCompany);
            } catch (error) {
                console.log("Error on checking if code is used", error);
                captureError(error, {
                    feature: 'event-management',
                    operation: 'check-invitation-code-usage',
                    context: { companyid }
                });
            }

               

            console.log("codes", codes);
            return codes;

        } catch (error) {
            console.log("error on fetching departments", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'fetch-invitation-codes',
                context: { companyid }
            });
        }
    }

    const getVisitors = async (codes, eventId) => {
        console.log("Loading tickets", codes)
        try {
            let allTickets = [];  // This will hold all tickets across all codes.
            let nextToken = null;
                do {
                    const ticketsRes = await client.graphql({
                        variables: { 
                            limit: 1000,
                            filter: {
                                ticketEventId: {
                                    eq: eventId
                                },
                                or: codes.map(code => ([
                                    {
                                        invitationCode: {
                                            eq: code.code
                                        }
                                    },
                                    {
                                        invitationCode: {
                                            eq: code.code.toUpperCase()
                                        }
                                    },
                                    {
                                        invitationCode: {
                                            eq: code.code.toLowerCase()
                                        }
                                    }
                                ])).flat()

                            },
                            nextToken: nextToken
                            
                        },
                        query: /* GraphQL */ `
                        query SearchTickets($filter: SearchableTicketFilterInput,  $limit: Int, $nextToken: String) {
                            searchTickets(filter: $filter, limit: $limit, nextToken: $nextToken) {
                                items {
                                    id
                                    invitationCode
                                    createdAt
                                    eventDays
                                    personalAddress {
                                        company
                                        firstName
                                        lastName
                                        salutation
                                        title
                                    }
                                   
                                }
                                nextToken
                            }
                        }
                        `
                    });
    
                    nextToken = ticketsRes.data.searchTickets.nextToken;
                    allTickets.push(...ticketsRes.data.searchTickets.items.map(i => ({...i, invitationCode: i.invitationCode?.toUpperCase() || ""})));
                } while (nextToken);
            
            setAllVisisions(allTickets);

            setGuests(allTickets);

        } catch (error) {
            console.log("Error on fetching tickets:", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'fetch-event-visitors',
                context: { eventId, codeCount: codes?.length }
            });
        }
    }

    const getAllEventVisitors = async (eventId) => {
        console.log("Loading tickets", codes)
        try {
            let allTickets = [];  // This will hold all tickets across all codes.
            let nextToken = null;
                do {
                    const ticketsRes = await client.graphql({
                        variables: { 
                            limit: 1000,
                            filter: {
                                ticketEventId: {
                                    eq: eventId
                                }
                            },
                            nextToken: nextToken
                            
                        },
                        query: /* GraphQL */ `
                        query SearchTickets($filter: SearchableTicketFilterInput,  $limit: Int, $nextToken: String) {
                            searchTickets(filter: $filter, limit: $limit, nextToken: $nextToken) {
                                items {
                                    id
                                    invitationCode
                                    createdAt
                                    eventDays
                                    personalAddress {
                                        company
                                        firstName
                                        lastName
                                        salutation
                                        title
                                    }
                                   
                                }
                                nextToken
                            }
                        }
                        `
                    });
    
                    nextToken = ticketsRes.data.searchTickets.nextToken;
                    allTickets.push(...ticketsRes.data.searchTickets.items.map(i => ({...i, invitationCode: i.invitationCode?.toUpperCase() || ""})));
                } while (nextToken);
            setAllVisisions(allTickets);
        } catch (error) {
            console.log("Error on fetching tickets:", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'fetch-all-event-visitors',
                context: { eventId }
            });
        }
    }

    const getEvents = async () => {
        try {
            const events = await client.graphql({
                query: /* GraphQL */ `
                query ListEvents {
                    listEvents {
                        items {
                            id
                            name {
                                text
                                language
                            }
                            startDate
                            endDate
                            status
                        }
                    }
                }
                `,
                variables: {
                    filter: {
                        startDate: {
                            ge: new Date().toISOString().substring(0, 10)
                        }
                    }
                }
            });
            setEvents(events.data.listEvents.items.filter(event => event.status === "active"));
            return events.data.listEvents.items;
        } catch (error) {
            console.log("error on fetching events", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'fetch-events'
            });
        }
    }

    const getCompanyBookedBooths = async () => {
        // Validate companyid before making GraphQL query
        if (!companyid) {
            console.error("Cannot fetch booked booths: companyid is null or undefined");
            captureError(new Error("Missing companyid in getCompanyBookedBooths"), {
                feature: 'event-management',
                operation: 'fetch-company-booked-booths',
                context: { companyid, pathname: window.location.pathname }
            });
            return;
        }

        try {
            const companyBookedBooths = await client.graphql({
                variables: { id: companyid },
                query: /* GraphQL */ `
                query GetCompany($id: ID!) {
                    getCompany(id: $id) {
                        id
                        bookedBooths {
                            items {
                                id
                                boothNumber
                                event {
                                    id
                                    name {
                                        text
                                        language
                                    }
                                    startDate
                                    endDate
                                }
                            }
                        }
                    }
                }
                `
            });
            setCompanyBookedBooths(companyBookedBooths.data.getCompany.bookedBooths.items);
            // Add the event ids of the booked booths to the companyAttendingEventsIds, create set to remove duplicates.
            setCompanyAttendingEventsIds([...new Set(companyBookedBooths.data.getCompany.bookedBooths.items.map(booth => booth.event.id))]);

        } catch (error) {
            console.log("error on fetching company booked booths", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'fetch-company-booked-booths',
                context: { companyid }
            });
        }
    }


    

    const loadData = async () => {
        setIsLoading(true);
        try {
            const codes = await getCodes();
            await getEvents();
            await getCompanyBookedBooths();
            
            
        } catch (error) {
            console.log("Error on loading data", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'load-guest-data',
                context: { companyid }
            });
        }
        setIsLoading(false);
    }



    useEffect(() => {
        loadData();
    }, [company]);

    useEffect(() => {
        if(selectedEventForVisitorsId) {
        getVisitors(codes, selectedEventForVisitorsId);
        }
    }, [codes, selectedEventForVisitorsId]);

   

   


    const [newCodeInput, setNewCodeInput] = useState("");
    const [newCodeEventId, setNewCodeEventId] = useState("");

    const handleAddCode = async () => {
        // Validate companyid before making GraphQL query
        if (!companyid) {
            console.error("Cannot add code: companyid is null or undefined");
            captureError(new Error("Missing companyid in handleAddCode"), {
                feature: 'event-management',
                operation: 'create-invitation-code',
                context: { companyid, pathname: window.location.pathname }
            });
            return;
        }

        try {
            const newCode = await client.graphql({
                variables: {
                    input: {
                        code: newCodeInput,
                        companyInvitationCodesId: companyid,
                        eventInvitationCodesId: newCodeEventId
                    }
                },
                query: /* GraphQL */ `
                mutation CreateInvitationCode($input: CreateInvitationCodeInput!) {
                    createInvitationCode(input: $input) {
                        id
                        code
                        event {
                            id
                            name {
                                text
                                language
                            }
                            startDate
                            endDate
                        }
                    }
                }
                `
            });
            setNewCodeInput("");
            setNewCodeEventId("");
            setCodes([...codes, newCode.data.createInvitationCode]);
        } catch (error) {
            console.log("Error on adding code", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'create-invitation-code',
                context: { companyid, newCodeEventId, code: newCodeInput }
            });
        }
    }

    const handleDeleteCode = async (codeId) => {
        try {
            await client.graphql({
                variables: {
                    input: {
                        id: codeId
                    }
                },
                query: /* GraphQL */ `
                mutation DeleteInvitationCode($input: DeleteInvitationCodeInput!) {
                    deleteInvitationCode(input: $input) {
                        id
                    }
                }
                `
            });
            setCodes(codes.filter(code => code.id !== codeId));
        } catch (error) {
            console.log("Error on deleting code", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'delete-invitation-code',
                context: { codeId }
            });
        }
    }






    const [basicActive, setBasicActive] = useState('tabGuests');

    const handleBasicClick = (value) => {
      if (value === basicActive) {
        return;
      }

      setBasicActive(value);
    };

    // Helper function to check if an event is still ongoing or in the future (endDate >= today)
    const isFutureEvent = (event) => {
        if (!event?.endDate) return false;
        const endDate = new Date(event.endDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return endDate >= today;
    };

    // Helper function to check if event ended within the last 30 days (for viewing guests)
    const isEventWithin30Days = (event) => {
        if (!event?.endDate) return false;
        const endDate = new Date(event.endDate);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return endDate >= thirtyDaysAgo;
    };

    // Only show codes for current/future events (endDate >= today)
    const currentEventCodes = codes.filter(code => isFutureEvent(code.event));

    // Filter events for "Add Code" dropdown: only future events (endDate >= today)
    const futureEventsForAddCode = events.filter(e =>
        companyAttendingEventsIds.includes(e.id) && isFutureEvent(e)
    );

    // Filter events for "Guests" dropdown: events within 30 days after end
    const eventsForGuestsView = events.filter(e =>
        companyAttendingEventsIds.includes(e.id) && isEventWithin30Days(e)
    );

    useEffect(() => {
        if(basicActive === 'tabAllVisitors') {
            getAllEventVisitors(selectedEventForVisitorsId);
        }
    }, [basicActive, selectedEventForVisitorsId]);

  return (
<>
<MDBTabs className='mb-3'>
        <MDBTabsItem>
          <MDBTabsLink onClick={() => handleBasicClick('tabGuests')} active={basicActive === 'tabGuests'}>
            {t("Own Guests")}
          </MDBTabsLink>
        </MDBTabsItem>
        <MDBTabsItem>
          <MDBTabsLink onClick={() => handleBasicClick('tabAllVisitors')} active={basicActive === 'tabAllVisitors'}>
            {t("All Visitors")}
          </MDBTabsLink>
        </MDBTabsItem>
      </MDBTabs>

      <MDBTabsContent>
        <MDBTabsPane open={basicActive === 'tabGuests'}>
            <MDBTable>
                <MDBTableHead>
                    <tr>
                    <th>{t("Code")}</th>
                    </tr>
                </MDBTableHead>
                <MDBTableBody>
                    {/* Only show codes for current/future events */}
                    {currentEventCodes.map((code) => (
                    <tr key={code.id}>
                        <td>
                            {code.code.toUpperCase()} - {code.event.name.find(n => n.language === i18n.language)?.text || code.event.name[0].text} ({new Date(code.event.startDate).toLocaleDateString("de-DE")} - {new Date(code.event.endDate).toLocaleDateString("de-DE")})
                            {code.discountCode && (
                                <span className="badge bg-success ms-2">
                                    <MDBIcon fas icon="gift" className="me-1" />
                                    {t("Free tickets")}: {code.discountCode.usageCount || 0} / {code.discountCode.maxUsage} {t("used")}
                                </span>
                            )}
                            {usedCodes.includes(code.id) ?
                            <span style={{float: "right"}}>
                            <MDBTooltip tag='span' title={t("Code is used and cannot be deleted.")}>
                                <MDBIcon fas icon="trash" style={{color: "gray"}} />
                            </MDBTooltip>
                            </span>
                            :
                            code.createdByAdmin ?
                            <span style={{float: "right"}}>
                            <MDBTooltip tag='span' title={t("Code is created by the system and cannot be deleted.")}>
                                <MDBIcon fas icon="trash" style={{color: "gray"}} />
                            </MDBTooltip>
                            </span>
                            :
                            <span style={{float: "right"}}>
                            <MDBIcon fas icon="trash" onClick={() => handleDeleteCode(code.id)} />
                            </span>
                            }
                            </td>
                    </tr>
                    ))}
                    <tr>
                        <MDBTypography tag="h6" className="my-0">{t("Add Code")}</MDBTypography>
                        <span>
                            {t("Please choose a code that is either random or not too general. It is best if the code also includes the name of the company.")}
                        </span>
                        <MDBRow>
                            <MDBCol>
                                <MDBInput label={t("Code")} value={newCodeInput} onChange={(e) => setNewCodeInput(e.target.value)} />
                            </MDBCol>
                            <MDBCol>
                                <MDBSelect
                                    label={t("Event")}
                                    data={[
                                        { text: t("Select Event"), value: "" },
                                        ...futureEventsForAddCode.map(event => ({ text: event.name.find(n => n.language === i18n.language)?.text || event.name[0].text, value: event.id }))
                                    ]}
                                    value={newCodeEventId}
                                    onChange={(e) => setNewCodeEventId(e.value)}
                                />
                            </MDBCol>
                            <MDBCol>
                                <MDBBtn onClick={handleAddCode}>{t("Add Code")}</MDBBtn>
                            </MDBCol>
                        </MDBRow>
                    </tr>
                </MDBTableBody>
            </MDBTable>
            <MDBTypography tag="h6" className="my-0">{t("Guests")}</MDBTypography>
            <MDBCard>
                <MDBCardBody>
                    <MDBRow>
                        <MDBCol lg="4">
                            <MDBSelect
                                label={t("Event")}
                                data={[
                                    { text: t("Select Event"), value: "" },
                                    ...eventsForGuestsView.map(event => ({ text: event.name.find(n => n.language === i18n.language)?.text || event.name[0].text, value: event.id }))
                                ]}
                                value={selectedEventForVisitorsId}
                                onChange={(e) => setSelectedEventForVisitorsId(e.value)}
                            />
                        </MDBCol>
                    </MDBRow>
                </MDBCardBody>
            </MDBCard>
            <MDBTable>
                <MDBTableHead>
                    <tr>
                    <th>{t("Code")}</th>
                    <th>{t("Guest")}</th>
                    <th>{t("Company")}</th>
                    <th>{t("Visiting Days")}</th>
                    <th>{t("Booking Date")}</th>
                    </tr>
                </MDBTableHead>
                <MDBTableBody>
                    {
                        guests.map((ticket) => (
                            <tr>
                                <td>{ticket.invitationCode}</td>
                                <td>{ticket.personalAddress.salutation} {ticket.personalAddress.firstName} {ticket.personalAddress.lastName}</td>
                                <td>{ticket.personalAddress.company || ""}</td>
                                <td>{ticket.eventDays && ticket.eventDays.length > 0 ? ticket.eventDays.map(day => day ? new Date(day).toLocaleDateString("de-DE") : "").join(", ") : ""}</td>
                                <td>{ticket.createdAt && new Date(ticket.createdAt).toLocaleDateString("de-DE")}</td>
                            </tr>
                        ))
                    }
                    {
                        isLoading && <tr><td colSpan="5">{t("Loading...")}</td></tr>
                    }
                </MDBTableBody>
            </MDBTable>
        </MDBTabsPane>
        <MDBTabsPane open={basicActive === 'tabAllVisitors'}>
        <MDBCard>
                <MDBCardBody>
                    <MDBRow>
                        <MDBCol lg="4">
                            <MDBSelect
                                label={t("Event")}
                                data={[
                                    { text: t("Select Event"), value: "" },
                                    ...eventsForGuestsView.map(event => ({ text: event.name.find(n => n.language === i18n.language)?.text || event.name[0].text, value: event.id }))
                                ]}
                                value={selectedEventForVisitorsId}
                                onChange={(e) => setSelectedEventForVisitorsId(e.value)}
                            />
                        </MDBCol>
                    </MDBRow>
                </MDBCardBody>
            </MDBCard>
            <MDBTable style={{
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
                userSelect: "none",
                
            }}>
                <MDBTableHead>
                    <tr>
                    <th>{t("Guest")}</th>
                    <th>{t("Company")}</th>
                    <th>{t("Visiting Days")}</th>
                    
                    </tr>
                </MDBTableHead>
                <MDBTableBody>
                    {
                        allVisisions.map((ticket) => (
                            <tr>
                                <td>{ticket.personalAddress?.salutation} {ticket.personalAddress?.firstName} {ticket.personalAddress?.lastName}</td>
                                <td>{ticket.personalAddress?.company || ""}</td>
                                <td>{ticket.eventDays && ticket.eventDays.length > 0 ? ticket.eventDays.map(day => day ? new Date(day).toLocaleDateString("de-DE") : "").join(", ") : ""}</td>
                               
                            </tr>
                        ))
                    }
                    {
                        isLoading && <tr><td colSpan="5">{t("Loading...")}</td></tr>
                    }
                </MDBTableBody>
            </MDBTable>
        </MDBTabsPane>
      </MDBTabsContent>


</>
  )
}
